/**
 * OptimizedImage — CDN-aware responsive image atom.
 *
 * For TrafiFlow CDN hosts, emits srcSet with `?width=` resize params
 * (async CDN image pipeline). Format is Accept-negotiated unless overridden.
 * External hosts pass through as plain src.
 *
 * Host apps pass `cdnHosts` from env (e.g. `VITE_CDN_HOSTS`). When omitted,
 * only the standard `cdn.*.trafiflow.com` host pattern is treated as resizable.
 */

'use client';

import { useCallback, useState, type CSSProperties, type ImgHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import {
  buildCdnImageUrl,
  buildSrcSet,
  DEFAULT_IMAGE_SIZES,
  DEFAULT_SRCSET_WIDTHS,
  isCdnResizable,
  type CdnImageFormat,
} from '../../utils/cdn-image-url';

export interface OptimizedImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'width' | 'height' | 'loading'> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  sizes?: string;
  /** Above-fold: eager + fetchpriority=high. */
  priority?: boolean;
  /** CSS aspect-ratio value, e.g. "3/4" or "16/9". Prevents CLS. */
  aspectRatio?: string;
  /** Optional format override. Omit to let the CDN negotiate from Accept. */
  format?: CdnImageFormat;
  widths?: readonly number[];
  /**
   * Optional TrafiFlow CDN host list from the host app (`VITE_CDN_HOSTS`).
   * Comma-separated string or array. Empty → pattern-only detection.
   */
  cdnHosts?: string[] | string;
  className?: string;
  onLoad?: () => void;
}

interface FadeImgProps {
  $loaded: boolean;
}

const Frame = styled.div<{ $aspectRatio?: string }>`
  position: relative;
  width: 100%;
  overflow: hidden;
  ${({ $aspectRatio }) => ($aspectRatio ? `aspect-ratio: ${$aspectRatio};` : '')}

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FadeImg = styled.img<FadeImgProps>`
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    opacity: 1;
  }
`;

export function OptimizedImage({
  src,
  alt,
  width,
  height,
  sizes = DEFAULT_IMAGE_SIZES,
  priority = false,
  aspectRatio,
  format,
  widths = DEFAULT_SRCSET_WIDTHS,
  cdnHosts = [],
  className,
  onLoad,
  style,
  ...rest
}: OptimizedImageProps) {
  const [loaded, setLoaded] = useState(false);
  const resizable = isCdnResizable(src, cdnHosts);

  const srcSet = resizable ? buildSrcSet(src, widths, format, cdnHosts) : undefined;
  const resolvedSrc = resizable
    ? buildCdnImageUrl(
        src,
        {
          width: width ?? widths[widths.length - 1],
          ...(format ? { format } : {}),
        },
        cdnHosts,
      )
    : src;

  const markLoaded = useCallback(() => {
    setLoaded((prev) => {
      if (!prev) onLoad?.();
      return true;
    });
  }, [onLoad]);

  const imgRef = useCallback(
    (el: HTMLImageElement | null) => {
      if (el?.complete && el.naturalWidth > 0) {
        markLoaded();
      }
    },
    [markLoaded],
  );

  const handleError = () => {
    setLoaded(true);
  };

  const imgStyle: CSSProperties = { ...style };

  const img = (
    <FadeImg
      {...rest}
      ref={imgRef}
      className={aspectRatio ? undefined : className}
      src={resolvedSrc}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : undefined}
      $loaded={loaded}
      style={imgStyle}
      onLoad={markLoaded}
      onError={handleError}
    />
  );

  if (!aspectRatio) {
    return img;
  }

  return (
    <Frame className={className} $aspectRatio={aspectRatio}>
      {img}
    </Frame>
  );
}

export default OptimizedImage;
